import React from "react";

const TOTAL_ROUNDS = 5;
const TARGET_POOL  = [3, 4, 5, 6, 7, 8, 9, 10, 12];
const BEST_KEY     = "audassist.timinggame.best";

function pickTargets() {
  return [...TARGET_POOL].sort(() => Math.random() - 0.5).slice(0, TOTAL_ROUNDS);
}

function loadBest() {
  try { return JSON.parse(localStorage.getItem(BEST_KEY)); } catch { return null; }
}

function maybeSetBest(total) {
  const prev = loadBest();
  if (prev === null || total < prev) {
    localStorage.setItem(BEST_KEY, JSON.stringify(total));
    return true;
  }
  return false;
}

function accuracyMeta(ms) {
  if (ms <= 30)  return { text: "Телепат!",  color: "#7c3aed" };
  if (ms <= 80)  return { text: "Идеально!", color: "#059669" };
  if (ms <= 200) return { text: "Отлично!",  color: "#2563eb" };
  if (ms <= 500) return { text: "Неплохо",   color: "#d97706" };
  return              { text: "Опоздал…",  color: "#e11d48" };
}

function formatTime(ms) {
  const totalCs = Math.floor(ms / 10);
  const cs = totalCs % 100;
  const s  = Math.floor(totalCs / 100) % 60;
  const m  = Math.floor(totalCs / 6000);
  return `${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}:${String(cs).padStart(2,"0")}`;
}

const r = () => Math.floor(Math.random() * 10);

export default function TimingGame() {
  const [targets, setTargets]         = React.useState(() => pickTargets());
  const [round, setRound]             = React.useState(0);
  const [phase, setPhase]             = React.useState("ready"); // ready | running | revealing | result | done
  const [results, setResults]         = React.useState([]);
  const [best, setBest]               = React.useState(loadBest);
  const [isNewBest, setIsNewBest]     = React.useState(false);
  const [timerDisplay, setTimerDisplay] = React.useState("00:00:00");
  const [isPressed, setIsPressed]     = React.useState(false);
  const startRef = React.useRef(null);

  const currentTarget = targets[round];
  const isRunning   = phase === "running";
  const isRevealing = phase === "revealing";
  const canClick    = phase === "ready" || phase === "running";
  const showPlay    = phase === "ready" || phase === "running" || phase === "revealing";

  // Scramble timer while running
  React.useEffect(() => {
    if (!isRunning) return;
    const id = setInterval(() => {
      setTimerDisplay(`${r()}${r()}:${r()}${r()}:${r()}${r()}`);
    }, 50);
    return () => clearInterval(id);
  }, [isRunning]);

  function handleButtonClick() {
    if (!canClick) return;
    if (phase === "ready") {
      startRef.current = performance.now();
      setTimerDisplay("00:00:00");
      setPhase("running");
    } else if (phase === "running") {
      const ms = performance.now() - startRef.current;
      setTimerDisplay(formatTime(ms));
      setPhase("revealing");

      setTimeout(() => {
        const diff = ms - currentTarget * 1000;
        const dev  = Math.round(Math.abs(diff));
        const next = [...results, { target: currentTarget, elapsed: ms, deviation: dev, diff }];
        setResults(next);

        if (round + 1 >= TOTAL_ROUNDS) {
          const total   = next.reduce((s, x) => s + x.deviation, 0);
          const newBest = maybeSetBest(total);
          if (newBest) { setBest(total); setIsNewBest(true); }
          setPhase("done");
        } else {
          setPhase("result");
        }
      }, 950);
    }
  }

  function handleNext() {
    setRound(v => v + 1);
    setTimerDisplay("00:00:00");
    setPhase("ready");
  }

  function handleRestart() {
    setTargets(pickTargets());
    setRound(0);
    setResults([]);
    setIsNewBest(false);
    setTimerDisplay("00:00:00");
    setPhase("ready");
  }

  const lastResult     = results[results.length - 1];
  const totalDeviation = results.reduce((s, x) => s + x.deviation, 0);

  const capGradient = isRunning
    ? "radial-gradient(ellipse at 36% 30%, #ff9999 0%, #ff2020 38%, #cc0000 66%, #8b0000 100%)"
    : "radial-gradient(ellipse at 36% 30%, #666 0%, #3a3a3a 38%, #222 66%, #0d0d0d 100%)";

  const capShadow = (pressed) => {
    if (pressed) {
      return isRunning
        ? "inset 0 4px 12px rgba(255,255,255,0.18), inset 0 -4px 10px rgba(0,0,0,0.45), 0 1px 0 #7a0000, 0 3px 10px rgba(150,0,0,0.35)"
        : "inset 0 4px 12px rgba(255,255,255,0.08), inset 0 -4px 10px rgba(0,0,0,0.45), 0 1px 0 #000, 0 2px 8px rgba(0,0,0,0.5)";
    }
    return isRunning
      ? "inset 0 8px 26px rgba(255,255,255,0.33), inset 0 -6px 14px rgba(0,0,0,0.28), 0 5px 0 #7a0000, 0 10px 30px rgba(180,0,0,0.5)"
      : "inset 0 8px 26px rgba(255,255,255,0.16), inset 0 -6px 14px rgba(0,0,0,0.3), 0 6px 0 #000, 0 10px 30px rgba(0,0,0,0.55)";
  };

  const textBtn = {
    display: "block", width: "100%", padding: "14px 0",
    borderRadius: 14, border: "none", cursor: "pointer",
    fontSize: 15, fontWeight: 600, letterSpacing: "0.01em",
    color: "#fff", background: "linear-gradient(135deg, #2AABEE 0%, #229ED9 100%)",
    boxShadow: "0 4px 16px rgba(34,158,217,0.35)",
    fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif",
  };

  return (
    <>
      <style>{`
        @keyframes timer-reveal {
          0%   { filter: blur(16px); opacity: 0.08; letter-spacing: 0.55em; transform: scaleX(1.14); }
          55%  { filter: blur(4px);  opacity: 0.72; letter-spacing: 0.14em; transform: scaleX(1.02); }
          100% { filter: blur(0);    opacity: 1;    letter-spacing: 0.08em; transform: scaleX(1); }
        }
        @keyframes scramble-flicker {
          0%, 100% { opacity: 1;   }
          50%      { opacity: 0.78; }
        }
      `}</style>

      <div style={{ minHeight: "70vh", display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', sans-serif" }}>
        <div style={{
          position: "relative",
          width: "100%",
          maxWidth: 360,
          background: "rgba(255,255,255,0.82)",
          backdropFilter: "blur(32px) saturate(180%)",
          WebkitBackdropFilter: "blur(32px) saturate(180%)",
          padding: "32px 28px 40px",
          borderRadius: 28,
          boxShadow: "0 2px 0 rgba(255,255,255,0.9) inset, 0 24px 64px rgba(0,0,0,0.10), 0 8px 24px rgba(0,0,0,0.06)",
          border: "1px solid rgba(255,255,255,0.75)",
        }}>

          {/* Round header */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
            <span style={{ fontSize: 17, fontWeight: 700, color: "#1c1c1e", letterSpacing: "-0.01em" }}>Не опоздай на пару</span>
            <span style={{ fontSize: 13, fontWeight: 500, color: "#8e8e93", background: "rgba(142,142,147,0.12)", borderRadius: 8, padding: "3px 9px" }}>
              {Math.min(round + 1, TOTAL_ROUNDS)} / {TOTAL_ROUNDS}
            </span>
          </div>

          {/* ── PLAY SCREEN ── */}
          {showPlay && (
            <div style={{ textAlign: "center" }}>
              {/* Target */}
              <div style={{
                fontSize: 96, fontWeight: 700, lineHeight: 1, marginBottom: 2,
                fontVariantNumeric: "tabular-nums", userSelect: "none",
                letterSpacing: "-0.04em",
                color: isRunning ? "transparent" : "#1c1c1e",
                transition: "color 0.35s ease",
              }}>
                {currentTarget}
              </div>
              <div style={{
                fontSize: 17, fontWeight: 400, marginBottom: 28,
                color: isRunning ? "transparent" : "#8e8e93",
                transition: "color 0.35s ease",
              }}>
                секунд
              </div>

              {/* Timer */}
              <div style={{
                fontFamily: "'SF Mono','Fira Mono','Courier New',monospace",
                fontSize: 38, fontWeight: 600,
                letterSpacing: "0.08em",
                fontVariantNumeric: "tabular-nums",
                color: isRunning ? "#c7c7cc" : "#1c1c1e",
                marginBottom: 36,
                transition: "color 0.3s",
                animation: isRunning
                  ? "scramble-flicker 0.09s ease-in-out infinite"
                  : isRevealing
                  ? "timer-reveal 0.92s cubic-bezier(0.22, 1, 0.36, 1) forwards"
                  : "none",
              }}>
                {timerDisplay}
              </div>

              {/* 3D Button */}
              <div style={{ display: "flex", justifyContent: "center" }}>
                <div
                  onMouseDown={() => canClick && setIsPressed(true)}
                  onMouseUp={() => setIsPressed(false)}
                  onMouseLeave={() => setIsPressed(false)}
                  onTouchStart={(e) => { e.preventDefault(); canClick && setIsPressed(true); }}
                  onTouchEnd={() => { setIsPressed(false); handleButtonClick(); }}
                  onClick={handleButtonClick}
                  style={{
                    position: "relative",
                    width: 166, height: 166,
                    cursor: canClick ? "pointer" : "default",
                    userSelect: "none",
                    opacity: isRevealing ? 0.55 : 1,
                    transition: "opacity 0.3s",
                  }}
                >
                  {/* Base rim (black) */}
                  <div style={{
                    position: "absolute", bottom: 0, left: "50%",
                    transform: "translateX(-50%)",
                    width: 166, height: 150,
                    borderRadius: "50%",
                    background: "radial-gradient(ellipse at 50% 58%, #2e2e2e 0%, #111 62%, #040404 100%)",
                    boxShadow: "0 8px 30px rgba(0,0,0,0.72), 0 2px 6px rgba(0,0,0,0.9)",
                  }} />

                  {/* Cap */}
                  <div style={{
                    position: "absolute", top: 0, left: "50%",
                    transform: `translateX(-50%) translateY(${isPressed && canClick ? 7 : 0}px)`,
                    width: 146, height: 146,
                    borderRadius: "50%",
                    background: capGradient,
                    boxShadow: capShadow(isPressed && canClick),
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "transform 0.08s ease, box-shadow 0.08s ease, background 0.4s ease",
                  }}>
                    <span style={{
                      fontSize: 18, fontWeight: 900, letterSpacing: "0.12em",
                      color: isRunning ? "rgba(255,255,255,0.96)" : "rgba(255,255,255,0.5)",
                      textShadow: isRunning ? "0 1px 5px rgba(0,0,0,0.55)" : "0 1px 3px rgba(0,0,0,0.9)",
                      transition: "color 0.4s",
                      userSelect: "none",
                    }}>
                      {isRunning ? "СТОП" : "СТАРТ"}
                    </span>
                  </div>
                </div>
              </div>

              {phase === "ready" && best !== null && (
                <div style={{ marginTop: 22, fontSize: 13, color: "#8e8e93", textAlign: "center" }}>
                  Рекорд: <span style={{ fontWeight: 600, color: "#3c3c43" }}>±{best} мс</span>
                </div>
              )}
            </div>
          )}

          {/* ── RESULT (mid-game) ── */}
          {phase === "result" && lastResult && (() => {
            const acc     = accuracyMeta(lastResult.deviation);
            const tooLate = lastResult.diff > 0;
            return (
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 30, fontWeight: 700, color: acc.color, marginBottom: 10, letterSpacing: "-0.01em" }}>
                  {acc.text}
                </div>
                <div style={{ fontSize: 62, fontWeight: 700, color: "#1c1c1e", lineHeight: 1, marginBottom: 8, fontVariantNumeric: "tabular-nums", fontFamily: "'SF Mono','Fira Mono',monospace", letterSpacing: "-0.02em" }}>
                  {lastResult.deviation < 1000
                    ? `${lastResult.deviation} мс`
                    : `${(lastResult.deviation / 1000).toFixed(2)} с`}
                </div>
                <div style={{ fontSize: 13, color: "#8e8e93", marginBottom: 4 }}>
                  {tooLate ? "+" : "−"}{Math.abs(Math.round(lastResult.diff))} мс — {tooLate ? "поздно" : "рано"}
                </div>
                <div style={{ fontSize: 13, color: "#c7c7cc", marginBottom: 36 }}>
                  {(lastResult.elapsed / 1000).toFixed(3)} с / цель {lastResult.target} с
                </div>
                <button onClick={handleNext} style={textBtn}>Следующий →</button>
              </div>
            );
          })()}

          {/* ── DONE ── */}
          {phase === "done" && (
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 500, color: "#8e8e93", marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Итого
              </div>
              <div style={{ fontSize: 58, fontWeight: 700, color: "#1c1c1e", lineHeight: 1, marginBottom: 4, fontVariantNumeric: "tabular-nums", letterSpacing: "-0.02em" }}>
                ±{totalDeviation} мс
              </div>
              <div style={{ fontSize: 13, color: "#8e8e93", marginBottom: isNewBest ? 8 : 20 }}>
                суммарное отклонение
              </div>
              {isNewBest && (
                <div style={{ fontSize: 13, fontWeight: 600, color: "#2AABEE", marginBottom: 20 }}>
                  Новый рекорд! 🎉
                </div>
              )}
              <div style={{ background: "rgba(142,142,147,0.08)", borderRadius: 16, overflow: "hidden", marginBottom: 20 }}>
                {results.map((res, i) => {
                  const acc = accuracyMeta(res.deviation);
                  return (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "11px 14px", borderBottom: i < results.length - 1 ? "1px solid rgba(142,142,147,0.12)" : "none", fontSize: 15 }}>
                      <span style={{ color: "#3c3c43", fontWeight: 400 }}>Раунд {i + 1} &nbsp;<span style={{ color: "#8e8e93", fontSize: 13 }}>{res.target} с</span></span>
                      <span style={{ fontWeight: 600, color: acc.color }}>±{res.deviation} мс</span>
                    </div>
                  );
                })}
              </div>
              <button onClick={handleRestart} style={textBtn}>Сыграть снова</button>
              {best !== null && !isNewBest && (
                <div style={{ marginTop: 16, fontSize: 13, color: "#8e8e93" }}>
                  Рекорд: <span style={{ fontWeight: 600, color: "#3c3c43" }}>±{best} мс</span>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
